/**
 * @flow
 * */
import { ACTION_STATUSES } from 'lib/core/actionStatuses';
import { initialSectionState } from 'store/reducers/initialStates';

import { brands } from 'modules/RiskWeeklyReport/settings';
import RISK_WEEKLY_ACTION_TYPES from '../actions/types';

const initialState = () => ({
  weeks: initialSectionState(),
  overview: initialSectionState(),
  // Keyed brand|department: the drilldown is opened per department and several can be expanded,
  // so one shared section would have them overwrite each other.
  departmentDetail: {},
  // The partner coverage probe. One section rather than keyed: it describes a single week and
  // ignores the brand and range filters, so a filter change does not make it stale.
  partnerCoverage: initialSectionState(),
  // DISABLED - sections for the five views the first production release does not ship.
  // RMT8993, 2026-09-14; see docs/report_docs/13-live-dashboard-only.md.
  // grmt: initialSectionState(),
  // // Keyed brand|setting|report|statusFilter. Keyed rather than single because each brand's table
  // // has its own open row, and keeping the filter in the key means flipping back to a filter you
  // // already looked at is instant instead of a refetch.
  // grmtSettingDetail: {},
  // employeeAnalytics: initialSectionState(),
  // // Keyed by grain: partner and trigger are two different groupings of the same window, so
  // // switching between them should not refetch what was already loaded.
  // partnerTrigger: {},
  // Not keyed: coverage describes the store, not a filtered view of it.
  calendar: initialSectionState(),
  // Keyed by grain, for the same reason: every Explore control except the grain re-pivots the cube
  // already in the store, so returning to a grain must not refetch it.
  exploreCube: {},
  // Statistics, keyed day|brand: flipping back to a day already seen shows it at once.
  dailyStatistics: {},
  // // Not keyed: Insights describes one week and ignores the brand and range filters, so a filter
  // // change does not make it stale the way it does every other section.
  // insights: initialSectionState(),
  // // Keyed brand|employeeId: both brands' tables can have a row open at once.
  // employeeDetail: {},
  // ---- Configuration management ----
  configOverview: initialSectionState(),
  // Keyed by area: the tabs are independent lists, each with its own search and page position,
  // so one shared section would make switching tabs lose the other's state.
  configPage: {},
  configUnmapped: initialSectionState(),
  // Keyed area|recordId, so a row's history panel and the area-wide log can be open together.
  configHistory: {},
  // The Clear cache control's own status and result. Its own section because it is neither a
  // fetch nor a config edit: nothing on screen is derived from it, but the button needs to
  // disable while it runs and to say what happened if the reload is refused.
  serverCache: initialSectionState(),
  // The write path's own status, plus the last result. Kept separate from the lists because a
  // save has to report warnings and a version bump that no list carries.
  configSave: initialSectionState(),

  filter: {
    brand: brands.both,
    weekFrom: null,
    weekTo: null
  }
});

const pending = (section) => ({ ...section, status: ACTION_STATUSES.PENDING, error: null });

const succeeded = (section, data) => ({
  ...section,
  status: ACTION_STATUSES.SUCCEED,
  data,
  error: null
});

const failed = (section, error) => ({
  ...section,
  status: ACTION_STATUSES.FAILED,
  error
});

const actionManager = {
  [RISK_WEEKLY_ACTION_TYPES.FETCH_WEEKS]: (state) => ({
    ...state,
    weeks: pending(state.weeks)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_WEEKS_SUCCESS]: (state, payload) => ({
    ...state,
    weeks: succeeded(state.weeks, payload.data)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_WEEKS_FAILURE]: (state, payload) => ({
    ...state,
    weeks: failed(state.weeks, payload.error)
  }),

  [RISK_WEEKLY_ACTION_TYPES.FETCH_OVERVIEW]: (state) => ({
    ...state,
    // The previous payload is kept while the next one loads, so switching the week range
    // dims the existing charts instead of blanking the page.
    overview: pending(state.overview)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_OVERVIEW_SUCCESS]: (state, payload) => ({
    ...state,
    overview: succeeded(state.overview, payload.data)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_OVERVIEW_FAILURE]: (state, payload) => ({
    ...state,
    overview: failed(state.overview, payload.error)
  }),

  [RISK_WEEKLY_ACTION_TYPES.FETCH_DEPARTMENT_DETAIL]: (state, payload) => ({
    ...state,
    departmentDetail: {
      ...state.departmentDetail,
      [payload.key]: pending(state.departmentDetail[payload.key] || initialSectionState())
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_DEPARTMENT_DETAIL_SUCCESS]: (state, payload) => ({
    ...state,
    departmentDetail: {
      ...state.departmentDetail,
      [payload.key]: succeeded(state.departmentDetail[payload.key] || initialSectionState(), payload.data)
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_DEPARTMENT_DETAIL_FAILURE]: (state, payload) => ({
    ...state,
    departmentDetail: {
      ...state.departmentDetail,
      [payload.key]: failed(state.departmentDetail[payload.key] || initialSectionState(), payload.error)
    }
  }),

  [RISK_WEEKLY_ACTION_TYPES.FETCH_PARTNER_COVERAGE]: (state) => ({
    ...state,
    partnerCoverage: pending(state.partnerCoverage)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_PARTNER_COVERAGE_SUCCESS]: (state, payload) => ({
    ...state,
    partnerCoverage: succeeded(state.partnerCoverage, payload.data)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_PARTNER_COVERAGE_FAILURE]: (state, payload) => ({
    ...state,
    partnerCoverage: failed(state.partnerCoverage, payload.error)
  }),

  // ===================================================================================
  // DISABLED - reducers for the five views the first production release does not ship.
  // RMT8993, 2026-09-14; see docs/report_docs/13-live-dashboard-only.md.
  // ===================================================================================
  // [RISK_WEEKLY_ACTION_TYPES.FETCH_GRMT]: (state) => ({
    // ...state,
    // grmt: pending(state.grmt)
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_GRMT_SUCCESS]: (state, payload) => ({
    // ...state,
    // grmt: succeeded(state.grmt, payload.data)
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_GRMT_FAILURE]: (state, payload) => ({
    // ...state,
    // grmt: failed(state.grmt, payload.error)
  // }),

  // [RISK_WEEKLY_ACTION_TYPES.FETCH_GRMT_SETTING_DETAIL]: (state, payload) => ({
    // ...state,
    // grmtSettingDetail: {
      // ...state.grmtSettingDetail,
      // [payload.key]: pending(state.grmtSettingDetail[payload.key] || initialSectionState())
    // }
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_GRMT_SETTING_DETAIL_SUCCESS]: (state, payload) => ({
    // ...state,
    // grmtSettingDetail: {
      // ...state.grmtSettingDetail,
      // [payload.key]: succeeded(
        // state.grmtSettingDetail[payload.key] || initialSectionState(), payload.data
      // )
    // }
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_GRMT_SETTING_DETAIL_FAILURE]: (state, payload) => ({
    // ...state,
    // grmtSettingDetail: {
      // ...state.grmtSettingDetail,
      // [payload.key]: failed(
        // state.grmtSettingDetail[payload.key] || initialSectionState(), payload.error
      // )
    // }
  // }),

  // [RISK_WEEKLY_ACTION_TYPES.FETCH_EMPLOYEE_ANALYTICS]: (state) => ({
    // ...state,
    // employeeAnalytics: pending(state.employeeAnalytics)
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_EMPLOYEE_ANALYTICS_SUCCESS]: (state, payload) => ({
    // ...state,
    // employeeAnalytics: succeeded(state.employeeAnalytics, payload.data)
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_EMPLOYEE_ANALYTICS_FAILURE]: (state, payload) => ({
    // ...state,
    // employeeAnalytics: failed(state.employeeAnalytics, payload.error)
  // }),

  // [RISK_WEEKLY_ACTION_TYPES.FETCH_EMPLOYEE_DETAIL]: (state, payload) => ({
    // ...state,
    // employeeDetail: {
      // ...state.employeeDetail,
      // [payload.key]: pending(state.employeeDetail[payload.key] || initialSectionState())
    // }
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_EMPLOYEE_DETAIL_SUCCESS]: (state, payload) => ({
    // ...state,
    // employeeDetail: {
      // ...state.employeeDetail,
      // [payload.key]: succeeded(
        // state.employeeDetail[payload.key] || initialSectionState(), payload.data
      // )
    // }
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_EMPLOYEE_DETAIL_FAILURE]: (state, payload) => ({
    // ...state,
    // employeeDetail: {
      // ...state.employeeDetail,
      // [payload.key]: failed(
        // state.employeeDetail[payload.key] || initialSectionState(), payload.error
      // )
    // }
  // }),

  // [RISK_WEEKLY_ACTION_TYPES.FETCH_PARTNER_TRIGGER]: (state, payload) => ({
    // ...state,
    // partnerTrigger: {
      // ...state.partnerTrigger,
      // [payload.key]: pending(state.partnerTrigger[payload.key] || initialSectionState())
    // }
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_PARTNER_TRIGGER_SUCCESS]: (state, payload) => ({
    // ...state,
    // partnerTrigger: {
      // ...state.partnerTrigger,
      // [payload.key]: succeeded(
        // state.partnerTrigger[payload.key] || initialSectionState(), payload.data
      // )
    // }
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_PARTNER_TRIGGER_FAILURE]: (state, payload) => ({
    // ...state,
    // partnerTrigger: {
      // ...state.partnerTrigger,
      // [payload.key]: failed(
        // state.partnerTrigger[payload.key] || initialSectionState(), payload.error
      // )
    // }
  // }),

  [RISK_WEEKLY_ACTION_TYPES.FETCH_CALENDAR]: (state) => ({
    ...state,
    calendar: pending(state.calendar)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CALENDAR_SUCCESS]: (state, payload) => ({
    ...state,
    calendar: succeeded(state.calendar, payload.data)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CALENDAR_FAILURE]: (state, payload) => ({
    ...state,
    calendar: failed(state.calendar, payload.error)
  }),

  // The saga is takeLatest, so a newer day cancels the one still loading and that one never
  // answers. Its section is put back to what it was before the request (its data if it had any,
  // otherwise not-started), or it would stay PENDING forever: the tab only loads a section with
  // no status, so returning to that day would show "Loading the day…" with Refresh disabled.
  [RISK_WEEKLY_ACTION_TYPES.FETCH_DAILY_STATISTICS]: (state, payload) => {
    const superseded = {};
    Object.keys(state.dailyStatistics).forEach((key) => {
      const section = state.dailyStatistics[key];
      superseded[key] = key !== payload.key && section && section.status === ACTION_STATUSES.PENDING
        ? { ...section, status: section.data ? ACTION_STATUSES.SUCCEED : null }
        : section;
    });

    return {
      ...state,
      dailyStatistics: {
        ...superseded,
        [payload.key]: pending(state.dailyStatistics[payload.key] || initialSectionState())
      }
    };
  },
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_DAILY_STATISTICS_SUCCESS]: (state, payload) => ({
    ...state,
    dailyStatistics: {
      ...state.dailyStatistics,
      [payload.key]: succeeded(state.dailyStatistics[payload.key] || initialSectionState(), payload.data)
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_DAILY_STATISTICS_FAILURE]: (state, payload) => ({
    ...state,
    dailyStatistics: {
      ...state.dailyStatistics,
      [payload.key]: failed(state.dailyStatistics[payload.key] || initialSectionState(), payload.error)
    }
  }),

  [RISK_WEEKLY_ACTION_TYPES.FETCH_EXPLORE_CUBE]: (state, payload) => ({
    ...state,
    exploreCube: {
      ...state.exploreCube,
      [payload.key]: pending(state.exploreCube[payload.key] || initialSectionState())
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_EXPLORE_CUBE_SUCCESS]: (state, payload) => ({
    ...state,
    exploreCube: {
      ...state.exploreCube,
      [payload.key]: succeeded(
        state.exploreCube[payload.key] || initialSectionState(), payload.data
      )
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_EXPLORE_CUBE_FAILURE]: (state, payload) => ({
    ...state,
    exploreCube: {
      ...state.exploreCube,
      [payload.key]: failed(
        state.exploreCube[payload.key] || initialSectionState(), payload.error
      )
    }
  }),

  [RISK_WEEKLY_ACTION_TYPES.CLEAR_SERVER_CACHE]: (state) => ({
    ...state,
    serverCache: pending(state.serverCache)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_CLEAR_SERVER_CACHE_SUCCESS]: (state, payload) => ({
    ...state,
    serverCache: succeeded(state.serverCache, payload.data)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_CLEAR_SERVER_CACHE_FAILURE]: (state, payload) => ({
    ...state,
    serverCache: failed(state.serverCache, payload.error)
  }),

  // [RISK_WEEKLY_ACTION_TYPES.FETCH_INSIGHTS]: (state) => ({
    // ...state,
    // insights: pending(state.insights)
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_INSIGHTS_SUCCESS]: (state, payload) => ({
    // ...state,
    // insights: succeeded(state.insights, payload.data)
  // }),
  // [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_INSIGHTS_FAILURE]: (state, payload) => ({
    // ...state,
    // insights: failed(state.insights, payload.error)
  // }),

  // ---- Configuration management ----

  [RISK_WEEKLY_ACTION_TYPES.FETCH_CONFIG_OVERVIEW]: (state) => ({
    ...state,
    configOverview: pending(state.configOverview)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CONFIG_OVERVIEW_SUCCESS]: (state, payload) => ({
    ...state,
    configOverview: succeeded(state.configOverview, payload.data)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CONFIG_OVERVIEW_FAILURE]: (state, payload) => ({
    ...state,
    configOverview: failed(state.configOverview, payload.error)
  }),

  [RISK_WEEKLY_ACTION_TYPES.FETCH_CONFIG_PAGE]: (state, payload) => ({
    ...state,
    configPage: {
      ...state.configPage,
      [payload.key]: pending(state.configPage[payload.key] || initialSectionState())
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CONFIG_PAGE_SUCCESS]: (state, payload) => ({
    ...state,
    configPage: {
      ...state.configPage,
      [payload.key]: succeeded(state.configPage[payload.key] || initialSectionState(), payload.data)
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CONFIG_PAGE_FAILURE]: (state, payload) => ({
    ...state,
    configPage: {
      ...state.configPage,
      [payload.key]: failed(state.configPage[payload.key] || initialSectionState(), payload.error)
    }
  }),

  [RISK_WEEKLY_ACTION_TYPES.FETCH_CONFIG_UNMAPPED]: (state) => ({
    ...state,
    configUnmapped: pending(state.configUnmapped)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CONFIG_UNMAPPED_SUCCESS]: (state, payload) => ({
    ...state,
    configUnmapped: succeeded(state.configUnmapped, payload.data)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CONFIG_UNMAPPED_FAILURE]: (state, payload) => ({
    ...state,
    configUnmapped: failed(state.configUnmapped, payload.error)
  }),

  [RISK_WEEKLY_ACTION_TYPES.SAVE_CONFIG_ROW]: (state) => ({
    ...state,
    configSave: pending(state.configSave)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_SAVE_CONFIG_ROW_SUCCESS]: (state, payload) => ({
    ...state,
    configSave: succeeded(state.configSave, payload.data),
    // The overview carries per-area counts and versions, both of which a save moves. Marking it
    // stale rather than patching it: the server is the authority on the new version number.
    configOverview: initialSectionState(),
    // The queue too. Any classification change can alter what is unmapped, and a mapped value
    // that stays on the queue looking unmapped is worse than a refetch nobody asked for.
    configUnmapped: initialSectionState(),
    // And every history panel: a rename writes changes into other areas' logs, so an open panel
    // would otherwise go on showing a history that is missing the version just saved.
    configHistory: {}
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_SAVE_CONFIG_ROW_FAILURE]: (state, payload) => ({
    ...state,
    configSave: failed(state.configSave, payload.error)
  }),

  [RISK_WEEKLY_ACTION_TYPES.DELETE_CONFIG_ROW]: (state) => ({
    ...state,
    configSave: pending(state.configSave)
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_DELETE_CONFIG_ROW_SUCCESS]: (state, payload) => ({
    ...state,
    configSave: succeeded(state.configSave, payload.data),
    configOverview: initialSectionState(),
    configUnmapped: initialSectionState(),
    configHistory: {}
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_DELETE_CONFIG_ROW_FAILURE]: (state, payload) => ({
    ...state,
    configSave: failed(state.configSave, payload.error)
  }),

  [RISK_WEEKLY_ACTION_TYPES.CLEAR_CONFIG_SAVE_RESULT]: (state) => ({
    ...state,
    configSave: initialSectionState()
  }),

  [RISK_WEEKLY_ACTION_TYPES.FETCH_CONFIG_HISTORY]: (state, payload) => ({
    ...state,
    configHistory: {
      ...state.configHistory,
      [payload.key]: pending(state.configHistory[payload.key] || initialSectionState())
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CONFIG_HISTORY_SUCCESS]: (state, payload) => ({
    ...state,
    configHistory: {
      ...state.configHistory,
      [payload.key]: succeeded(state.configHistory[payload.key] || initialSectionState(), payload.data)
    }
  }),
  [RISK_WEEKLY_ACTION_TYPES.SET_FETCH_CONFIG_HISTORY_FAILURE]: (state, payload) => ({
    ...state,
    configHistory: {
      ...state.configHistory,
      [payload.key]: failed(state.configHistory[payload.key] || initialSectionState(), payload.error)
    }
  }),

  [RISK_WEEKLY_ACTION_TYPES.SET_FILTER]: (state, payload) => ({
    ...state,
    filter: { ...state.filter, ...payload },
    // Drilldown rows and grain-keyed payloads are scoped to the window, so a filter change
    // makes them stale.
    departmentDetail: {},
    grmtSettingDetail: {},
    employeeDetail: {},
    partnerTrigger: {},
    exploreCube: {}
  }),

  [RISK_WEEKLY_ACTION_TYPES.CLEAR_STATE]: () => initialState()
};

export default (state:Object = initialState(), { type, payload }: Object = {}) => {
  const handler = actionManager[type];
  return handler ? handler(state, payload) : state;
};
